import localforage from 'localforage'
import { Advice } from '@/composables/useAdvices'
import { ComputedRef, computed, reactive, toRefs, ref, toRaw } from 'vue'
import { UploadedFileType } from '@/types/partials'
import { useApi } from './useApi'

const { usePost, usePatch } = useApi()

const namespace = `${process.env.PACKAGE_NAME || 'app'}`

export enum InvoiceFormModes {
    ADD = 'add',
    EDIT = 'edit',
    CORRECTION = 'correction',
}
export type Base64PageImg = {
    filename: string
    img: string
}
export type AttachmentDescription = {
    filename: string
    base64: string
    // this is going to be serverd to backend so camelcase it is
    // eslint-disable-next-line camelcase
    advice_ids: number[]
    // eslint-disable-next-line camelcase
    document_types: number[]
}
export type GroupedAttachmentDescription = {
    attachmentId: string | number
    pages: AttachmentDescription[]
}
export type PaginatedAttachment = {
    attachmentId: string | number
    paginatedFile: Base64PageImg[]
    pageCount: number
}
export type AdviceInInvoiceDetails = {
    id: number
    axId: string
    deliveryDate: string
    loadingDate: string
    quantity: number
    driver: string
    vehicleIdentificationNumber: string
}
export type InvoiceAttachmentType = {
    filename: string
    base64: string
}
export interface IPostInvoice {
    id?: number | undefined
    number: string
    ksefNumber: string
    amount: number
    filenamePdf: string
    base64Pdf: string
    adviceIds: number[]
    deliveryReactionImagesIds?: number[]
    attachments: AttachmentDescription[] | { id: number }[]
}

interface IInvoice {
    invoiceFile: UploadedFileType | Record<string, never>
    attachmentFiles: UploadedFileType[]
    paginatedAttachmentFiles: PaginatedAttachment[]
    attachmentDescriptions: GroupedAttachmentDescription[]
    advices: AdviceInInvoiceDetails[]
    invoiceValue: string
    invoiceFormMode: InvoiceFormModes
    editedInvoiceName: string
    correctionFile: UploadedFileType | Record<string, never>
    correctionValue: string
    editedInvoiceId: number
    invoiceNumber: string
    ksefNumber: string
}
const invoice: IInvoice = reactive({
    invoiceFile: {},
    attachmentFiles: [],
    paginatedAttachmentFiles: [],
    attachmentDescriptions: [],
    advices: [],
    invoiceValue: '',
    invoiceNumber: '',
    ksefNumber: '',
    invoiceFormMode: InvoiceFormModes.ADD,
    editedInvoiceName: '',
    correctionFile: {},
    correctionValue: '',
    editedInvoiceId: 0,
})

const isLoading = ref<boolean>(false)
const localForageInvoiceDataName = 'invoiceData'

export const useAdviceDocuments = (): {
    selectedAdvices: ComputedRef<AdviceInInvoiceDetails[]>
    addAdviceToStorage: (advice: Advice, saveInLocalForage?: boolean) => Promise<void>
    removeAdviceFromStorage: (adviceId: number) => Promise<void>
    removeAllSelectedAdvicesFromStorage: () => Promise<void>
    invoiceFile: ComputedRef<UploadedFileType | Record<string, never>>
    addInvoiceFile: (file: UploadedFileType) => Promise<void>
    removeInvoiceFile: () => Promise<void>
    attachmentFiles: ComputedRef<UploadedFileType[]>
    addAttachmentFiles: (files: UploadedFileType[]) => void
    removeAttachmentFile: (id: string | number) => void
    removeAllAttachmentFiles: () => void
    attachmentDescriptions: ComputedRef<GroupedAttachmentDescription[]>
    changePaginatedAttachmentDescription: (
        id: string | number,
        data: {
            page: number
            filename: string
            base64: string
            docType?: number
            adviceId?: number
        },
    ) => void
    paginatedAttachmentFiles: ComputedRef<PaginatedAttachment[]>
    addPaginatedAttachment: (
        base64StringArray: Base64PageImg[],
        attachmentId: string | number,
    ) => void
    removePaginatedAttachment: (id: string | number) => void
    invoiceValue: ComputedRef<string>
    setInvoiceValue: (invoiceValue: string) => void
    fetchInvoiceData: () => Promise<void>
    invoiceData: ComputedRef<IInvoice>
    addInvoice: (invoice: IPostInvoice) => Promise<void>
    editInvoice: (invoice: IPostInvoice) => Promise<void>
    addInvoiceCorrection: (invoice: IPostInvoice) => Promise<void>
    clearInvoiceData: () => void
    isLoading: ComputedRef<boolean>
    setInvoiceFormMode: (mode: InvoiceFormModes) => void
    invoiceFormMode: ComputedRef<InvoiceFormModes>
    editedInvoiceName: ComputedRef<string>
    setEditedInvoiceName: (invoiceName: string) => void
    setEditedInvoiceId: (invoiceId: number) => void
    editedInvoiceId: ComputedRef<number>
    clearEditedInvoiceName: () => void
    addCorrectionFile: (file: UploadedFileType) => Promise<void>
    removeCorrectionFile: () => Promise<void>
    correctionFile: ComputedRef<UploadedFileType | Record<string, never>>
    setCorrectionValue: (correctionValue: string) => void
    setInvoiceNumber: (invoiceNumber: string) => void
    correctionValue: ComputedRef<string>
    invoiceNumber: ComputedRef<string>
    ksefNumber: ComputedRef<string>
    validate: (attachment: UploadedFileType) => ComputedRef<boolean>
    validateAll: ComputedRef<boolean>
    setKsefNumber: (ksefNumber: string) => void
} => {
    // advices
    const addAdviceToStorage = async (advice: Advice, saveInLocalForage = true) => {
        const { advices } = toRefs(invoice)

        if (!Array.isArray(advices.value)) {
            advices.value = []
        }
        advices.value.push(mapAdviceItemToInvoiceDetails(advice))

        if (saveInLocalForage) {
            await refreshInvoiceDataInLocalForage()
        }
    }
    const removeAllSelectedAdvicesFromStorage = async () => {
        const { advices } = toRefs(invoice)
        advices.value = []
        await refreshInvoiceDataInLocalForage()
    }
    const removeAdviceFromStorage = async (adviceId: number) => {
        const invoiceData = (await localforage.getItem(
            `${namespace}.${localForageInvoiceDataName}`,
        )) as IInvoice
        const currentAdvices = invoiceData?.advices as AdviceInInvoiceDetails[]
        const { advices } = toRefs(invoice)

        if (Array.isArray(currentAdvices)) {
            advices.value = currentAdvices.filter(e => e.id !== adviceId)
        }
        await refreshInvoiceDataInLocalForage()
    }

    // invoice
    const addInvoiceFile = async (file: UploadedFileType) => {
        const { invoiceFile } = toRefs(invoice)
        invoiceFile.value = file
    }
    const removeInvoiceFile = async () => {
        const { invoiceFile } = toRefs(invoice)
        invoiceFile.value = {}
    }
    // attachments
    const addAttachmentFiles = (files: UploadedFileType[]) => {
        const { attachmentFiles } = toRefs(invoice)

        const currentFiles = attachmentFiles.value
        const filesToSave = currentFiles && currentFiles[0] ? [...currentFiles, ...files] : files

        attachmentFiles.value = filesToSave
    }
    const removeAttachmentFile = (id: string | number) => {
        const { attachmentFiles } = toRefs(invoice)
        const currentFiles = attachmentFiles.value as UploadedFileType[]
        const filteredItems = currentFiles.filter(e => e?.id !== id)

        removePaginatedAttachment(id)
        attachmentFiles.value = filteredItems
    }
    const removeAllAttachmentFiles = () => {
        const { attachmentFiles } = toRefs(invoice)
        attachmentFiles.value = []
    }
    const addPaginatedAttachment = (
        base64ImgArray: Base64PageImg[],
        attachmentId: string | number,
    ) => {
        const { paginatedAttachmentFiles, attachmentFiles } = toRefs(invoice)
        if (!attachmentFiles.value.some(e => e?.id === attachmentId)) {
            // if there's no attachment with given id don't add anything
            return
        }

        const arrayWithIdentifier = {
            attachmentId,
            paginatedFile: base64ImgArray,
            pageCount: base64ImgArray.length,
        }
        const currentFiles = paginatedAttachmentFiles.value
        const filesToSave =
            currentFiles && currentFiles[0]
                ? [...currentFiles, arrayWithIdentifier]
                : [arrayWithIdentifier]

        paginatedAttachmentFiles.value = filesToSave
    }

    const removePaginatedAttachment = (id: string | number) => {
        const { paginatedAttachmentFiles } = toRefs(invoice)

        const currentFiles = paginatedAttachmentFiles.value
        const filteredItems = currentFiles.filter(e => e?.attachmentId !== id)

        paginatedAttachmentFiles.value = filteredItems
    }

    const changePaginatedAttachmentDescription = (
        id: string | number,
        data: {
            page: number
            filename: string
            base64: string
            docType?: number
            adviceId?: number
        },
    ) => {
        const { attachmentDescriptions } = toRefs(invoice)
        const description = attachmentDescriptions.value.find(ad => ad.attachmentId === id)

        const newPageDescription: AttachmentDescription = {
            filename: data.filename,
            document_types: data.docType ? [data.docType] : [],
            advice_ids: data.adviceId ? [data.adviceId] : [],
            base64: data.base64,
        }
        const newDescription: GroupedAttachmentDescription = {
            attachmentId: id,
            pages: [],
        }
        if (description) {
            // if description exists edit data
            const page = description.pages[data.page - 1]
            if (page) {
                // if page already exists edit data
                if (data.docType) {
                    newPageDescription.document_types =
                        page.document_types.indexOf(data.docType) === -1
                            ? [...page.document_types, data.docType]
                            : page.document_types.filter(ai => ai !== data.docType)
                    newPageDescription.advice_ids = page.advice_ids || []
                }
                if (data.adviceId) {
                    newPageDescription.advice_ids =
                        page.advice_ids.indexOf(data.adviceId) === -1
                            ? [...page.advice_ids, data.adviceId]
                            : page.advice_ids.filter(ai => ai !== data.adviceId)
                    newPageDescription.document_types = page.document_types || []
                }
            } else {
                // if not create data
                if (data.docType) {
                    newPageDescription.document_types = [data.docType]
                }
                if (data.adviceId) {
                    newPageDescription.advice_ids = [data.adviceId]
                }
            }
        }
        const descriptionIndex = attachmentDescriptions.value.findIndex(
            ad => newDescription.attachmentId === ad.attachmentId,
        )
        newDescription.pages =
            descriptionIndex === -1 ? [] : attachmentDescriptions.value[descriptionIndex].pages
        newDescription.pages[data.page - 1] = newPageDescription
        descriptionIndex === -1
            ? attachmentDescriptions.value.push(newDescription)
            : (attachmentDescriptions.value[descriptionIndex] = newDescription)
    }
    // invoice value
    const setInvoiceValue = (value: string) => {
        const { invoiceValue } = toRefs(invoice)
        invoiceValue.value = value
    }
    // invoice number
    const setInvoiceNumber = (value: string) => {
        const { invoiceNumber } = toRefs(invoice)
        invoiceNumber.value = value
    }
    // invoice number
    const setKsefNumber = (value: string) => {
        const { ksefNumber } = toRefs(invoice)
        ksefNumber.value = value
    }
    // correction file
    const addCorrectionFile = async (file: UploadedFileType) => {
        const { correctionFile } = toRefs(invoice)
        correctionFile.value = file
    }
    const removeCorrectionFile = async () => {
        const { correctionFile } = toRefs(invoice)
        correctionFile.value = {}
    }

    // correction value
    const setCorrectionValue = (value: string) => {
        const { correctionValue } = toRefs(invoice)
        correctionValue.value = value
    }
    // helpers

    const fetchInvoiceData = async () => {
        const localForageInvoiceData = (await localforage.getItem(
            `${namespace}.${localForageInvoiceDataName}`,
        )) as IInvoice
        const { advices } = toRefs(invoice)

        if (localForageInvoiceData?.advices.length > 0) {
            advices.value = localForageInvoiceData.advices
        }
    }

    const refreshInvoiceDataInLocalForage = async () => {
        const advices = toRaw(invoice.advices)

        await localforage.setItem(`${namespace}.${localForageInvoiceDataName}`, {
            advices: advices,
        })
    }

    const mapAdviceItemToInvoiceDetails = function (advice: Advice): AdviceInInvoiceDetails {
        return {
            id: advice.id,
            axId: advice.axId,
            deliveryDate: advice.deliveryDate,
            loadingDate: advice.loadingDate,
            driver: `${advice.driverFirstName} ${advice.driverLastName}`,
            vehicleIdentificationNumber: advice.vehicleIdentificationNumber,
            quantity: Number(advice.quantity),
        }
    }

    const addInvoice = async (payloadInvoice: IPostInvoice) => {
        isLoading.value = true
        try {
            await usePost('/invoices', payloadInvoice)
        } finally {
            isLoading.value = false
        }
    }
    const editInvoice = async (invoice: IPostInvoice) => {
        isLoading.value = true
        try {
            await usePatch(`/invoices/${invoice.id}`, invoice)
        } finally {
            isLoading.value = false
        }
    }
    const addInvoiceCorrection = async (invoice: IPostInvoice) => {
        isLoading.value = true
        try {
            await usePost(`/invoices/correction/${invoice.id}`, invoice)
        } finally {
            isLoading.value = false
        }
    }
    const clearInvoiceData = () => {
        const {
            attachmentFiles,
            invoiceValue,
            advices,
            invoiceFile,
            correctionFile,
            correctionValue,
            invoiceNumber,
            ksefNumber,
            paginatedAttachmentFiles,
            attachmentDescriptions,
        } = toRefs(invoice)

        advices.value = []
        invoiceFile.value = {}
        attachmentFiles.value = []
        invoiceValue.value = ''
        correctionValue.value = ''
        invoiceNumber.value = ''
        ksefNumber.value = ''
        correctionFile.value = {}
        paginatedAttachmentFiles.value = []
        attachmentDescriptions.value = []
    }

    const setInvoiceFormMode = (mode: InvoiceFormModes) => {
        const { invoiceFormMode } = toRefs(invoice)
        invoiceFormMode.value = mode
    }
    const setEditedInvoiceName = (invoiceName: string) => {
        const { editedInvoiceName } = toRefs(invoice)
        editedInvoiceName.value = invoiceName
    }
    const setEditedInvoiceId = (invoiceId: number) => {
        const { editedInvoiceId } = toRefs(invoice)
        editedInvoiceId.value = invoiceId
    }

    const clearEditedInvoiceName = () => {
        setEditedInvoiceName('')
    }
    const validate = (attachment: UploadedFileType) => {
        const file = invoice.paginatedAttachmentFiles.find(
            paf => paf.attachmentId === attachment?.id,
        )
        const description = invoice.attachmentDescriptions.find(
            att => att.attachmentId === attachment?.id,
        )
        if (!file || !description) {
            return false
        }
        if (file.pageCount !== description.pages.length) {
            return false
        }
        if (
            description.pages.findIndex(
                item => item.document_types.length === 0 || item.advice_ids.length === 0,
            ) !== -1
        ) {
            return false
        }
        return true
    }
    const validateAll = () => {
        if (
            invoice.invoiceFormMode === InvoiceFormModes.CORRECTION &&
            invoice.attachmentFiles.length === 0
        ) {
            return true
        }
        return (
            invoice.attachmentFiles.length > 0 &&
            invoice.attachmentFiles.findIndex(item => validate(item) === false) === -1
        )
    }
    return {
        selectedAdvices: computed(() => invoice.advices),
        addAdviceToStorage,
        removeAdviceFromStorage,
        invoiceFile: computed(() => invoice.invoiceFile),
        addInvoiceFile,
        removeInvoiceFile,
        attachmentFiles: computed(() => invoice.attachmentFiles),
        addAttachmentFiles,
        removeAttachmentFile,
        removeAllAttachmentFiles,
        paginatedAttachmentFiles: computed(() => invoice.paginatedAttachmentFiles),
        addPaginatedAttachment,
        removePaginatedAttachment,
        attachmentDescriptions: computed(() => invoice.attachmentDescriptions),
        changePaginatedAttachmentDescription,
        removeAllSelectedAdvicesFromStorage,
        invoiceValue: computed(() => invoice.invoiceValue),
        setInvoiceValue,
        setInvoiceNumber,
        setKsefNumber,
        fetchInvoiceData,
        invoiceData: computed(() => invoice),
        addInvoice,
        editInvoice,
        addInvoiceCorrection,
        clearInvoiceData,
        isLoading: computed(() => isLoading.value),
        setInvoiceFormMode,
        invoiceFormMode: computed(() => invoice.invoiceFormMode),
        editedInvoiceName: computed(() => invoice.editedInvoiceName),
        setEditedInvoiceName,
        setEditedInvoiceId,
        editedInvoiceId: computed(() => invoice.editedInvoiceId),
        clearEditedInvoiceName,
        addCorrectionFile,
        removeCorrectionFile,
        correctionFile: computed(() => invoice.correctionFile),
        setCorrectionValue,
        correctionValue: computed(() => invoice.correctionValue),
        invoiceNumber: computed(() => invoice.invoiceNumber),
        ksefNumber: computed(() => invoice.ksefNumber),

        validate: attachment => computed(() => validate(attachment)),
        validateAll: computed(() => validateAll()),
    }
}
